// Commented out for future use
// interface LinkingRule {
//   expenseVendor: string;
//   transactionNames: string[];
//   amountTolerance: number; // percentage (0.1 = 10%)
//   confidence: number;
// }

interface LinkingPreference {
  id: string;
  expenseVendor: string;
  expenseName: string;
  transactionName: string;
  merchantName: string;
  amountDifference: number;
  createdAt: Date;
  usageCount: number;
}

class LinkingService {
  private readonly STORAGE_KEY = "expense_transaction_linking_prefs";
  private preferences: LinkingPreference[] = [];

  constructor() {
    this.loadPreferences();
  }

  private loadPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.preferences = JSON.parse(stored).map((p: any) => ({
          ...p,
          createdAt: new Date(p.createdAt),
        }));
      }
    } catch (error) {
      console.error("Failed to load linking preferences:", error);
      this.preferences = [];
    }
  }

  private savePreferences(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.preferences));
    } catch (error) {
      console.error("Failed to save linking preferences:", error);
    }
  }

  // Learn from manual linking
  learnFromLink(
    expenseVendor: string,
    expenseName: string,
    transactionName: string,
    merchantName: string,
    expenseAmount: number,
    transactionAmount: number
  ): void {
    const amountDifference = Math.abs(expenseAmount - transactionAmount);

    // Check if similar preference exists
    const existingIndex = this.preferences.findIndex(
      (p) =>
        p.expenseVendor.toLowerCase() === expenseVendor.toLowerCase() &&
        p.transactionName.toLowerCase() === transactionName.toLowerCase()
    );

    if (existingIndex >= 0) {
      // Update existing preference
      this.preferences[existingIndex].usageCount++;
      this.preferences[existingIndex].amountDifference =
        (this.preferences[existingIndex].amountDifference + amountDifference) /
        2; // Average
    } else {
      // Create new preference
      const newPreference: LinkingPreference = {
        id: Date.now().toString(),
        expenseVendor,
        expenseName,
        transactionName,
        merchantName,
        amountDifference,
        createdAt: new Date(),
        usageCount: 1,
      };
      this.preferences.push(newPreference);
    }

    // Keep only top 100 preferences by usage
    this.preferences.sort((a, b) => b.usageCount - a.usageCount);
    this.preferences = this.preferences.slice(0, 100);

    this.savePreferences();
  }

  // Fuzzy match transactions to expenses
  findBestMatches(
    expenseVendor: string,
    expenseName: string,
    expenseAmount: number,
    transactions: Array<{
      id: string;
      name: string;
      merchant_name?: string;
      amount: number;
      date: string;
    }>
  ): Array<{ transaction: any; confidence: number; reason: string }> {
    const matches: Array<{
      transaction: any;
      confidence: number;
      reason: string;
    }> = [];

    for (const transaction of transactions) {
      let confidence = 0;
      const reasons: string[] = [];

      // Check learned preferences first
      const learnedMatch = this.preferences.find(
        (p) =>
          p.expenseVendor.toLowerCase() === expenseVendor.toLowerCase() &&
          (transaction.name
            .toLowerCase()
            .includes(p.transactionName.toLowerCase()) ||
            transaction.merchant_name
              ?.toLowerCase()
              .includes(p.transactionName.toLowerCase()))
      );

      if (learnedMatch) {
        confidence += 0.4 + learnedMatch.usageCount * 0.1; // Up to 90% confidence
        reasons.push(`Previously linked ${learnedMatch.usageCount} times`);
      }

      // Vendor/merchant name matching
      const vendorLower = expenseVendor.toLowerCase();
      const transactionNameLower = transaction.name.toLowerCase();
      const merchantNameLower = transaction.merchant_name?.toLowerCase() || "";

      if (
        transactionNameLower.includes(vendorLower) ||
        merchantNameLower.includes(vendorLower)
      ) {
        confidence += 0.3;
        reasons.push("Vendor name match");
      } else if (
        this.fuzzyStringMatch(vendorLower, transactionNameLower) > 0.7 ||
        this.fuzzyStringMatch(vendorLower, merchantNameLower) > 0.7
      ) {
        confidence += 0.2;
        reasons.push("Fuzzy vendor match");
      }

      // Expense name matching
      const expenseNameWords = expenseName.toLowerCase().split(" ");
      const matchingWords = expenseNameWords.filter(
        (word) =>
          word.length > 3 &&
          (transactionNameLower.includes(word) ||
            merchantNameLower.includes(word))
      );

      if (matchingWords.length > 0) {
        confidence += (matchingWords.length / expenseNameWords.length) * 0.2;
        reasons.push(`${matchingWords.length} keyword matches`);
      }

      // Amount matching with learned tolerance or default
      const learnedTolerance = learnedMatch
        ? learnedMatch.amountDifference + 5
        : null;
      const amountDiff = Math.abs(expenseAmount - transaction.amount);

      if (amountDiff === 0) {
        confidence += 0.3;
        reasons.push("Exact amount match");
      } else if (learnedTolerance && amountDiff <= learnedTolerance) {
        confidence += 0.25;
        reasons.push("Amount within learned tolerance");
      } else if (amountDiff <= Math.max(expenseAmount * 0.2, 10)) {
        confidence += 0.15;
        reasons.push("Amount within 20% tolerance");
      } else if (amountDiff <= Math.max(expenseAmount * 0.5, 25)) {
        confidence += 0.05;
        reasons.push("Amount within 50% tolerance");
      }

      // Date proximity bonus (more recent = better)
      const daysDiff =
        Math.abs(new Date().getTime() - new Date(transaction.date).getTime()) /
        (1000 * 60 * 60 * 24);
      if (daysDiff <= 7) {
        confidence += 0.1;
        reasons.push("Recent transaction");
      } else if (daysDiff <= 30) {
        confidence += 0.05;
        reasons.push("Within 30 days");
      }

      // Only include matches with reasonable confidence
      if (confidence >= 0.3) {
        matches.push({
          transaction,
          confidence: Math.min(confidence, 1.0),
          reason: reasons.join(", "),
        });
      }
    }

    // Sort by confidence and return top matches
    return matches.sort((a, b) => b.confidence - a.confidence).slice(0, 5);
  }

  // Simple fuzzy string matching
  private fuzzyStringMatch(str1: string, str2: string): number {
    if (str1 === str2) return 1;
    if (str1.length === 0 || str2.length === 0) return 0;

    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1;

    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = Array(str2.length + 1)
      .fill(null)
      .map(() => Array(str1.length + 1).fill(null));

    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;

    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        if (str1[i - 1] === str2[j - 1]) {
          matrix[j][i] = matrix[j - 1][i - 1];
        } else {
          matrix[j][i] = Math.min(
            matrix[j - 1][i - 1] + 1, // substitution
            matrix[j][i - 1] + 1, // insertion
            matrix[j - 1][i] + 1 // deletion
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }

  // Get suggested transactions for an expense
  getSuggestedTransactions(
    expenseVendor: string,
    expenseName: string,
    expenseAmount: number,
    transactions: Array<{
      id: string;
      name: string;
      merchant_name?: string;
      amount: number;
      date: string;
    }>
  ): Array<{ transaction: any; confidence: number; reason: string }> {
    return this.findBestMatches(
      expenseVendor,
      expenseName,
      expenseAmount,
      transactions
    );
  }

  // Clear preferences (for testing/reset)
  clearPreferences(): void {
    this.preferences = [];
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // Get current preferences (for debugging)
  getPreferences(): LinkingPreference[] {
    return [...this.preferences];
  }
}

export const linkingService = new LinkingService();
export type { LinkingPreference };
